import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Dialog } from 'primereact/dialog';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Message } from 'primereact/message';
import { Toast } from 'primereact/toast';
import { Avatar } from 'primereact/avatar';
import { GlobalInputField } from '../../../components/GlobalComponents/GlobalInputField';
import { GlobalButton } from '../../../components/GlobalComponents/GlobalButton';
import apiClient from '../../../stateManagers/apiSetup/axiosInterceptor';

interface Subject {
  subject_id: string;
  subject_name: string;
  max_marks: number;
  practical_max_marks: number;
}

interface StudentLite {
  student_id: string;
  name: string;
  student_custom_id?: string;
  profilePicture?: any;
}

interface SubjectMarksEntry {
  theory: string;
  practical: string;
  updatedAt?: string | null;
}

type MarkField = 'theory' | 'practical';

// ── Design tokens (shared with AddNoticeModal) ────────────────────────────────
const C = {
  navy:       '#0e225d',
  navyMid:    '#1a3370',
  navyDark:   '#0a1a3d',
  gold:       '#fbb21f',
  goldLight:  '#ffc93c',
  goldDim:    'rgba(251,178,31,0.18)',
  goldBorder: 'rgba(251,178,31,0.35)',
  white:      '#ffffff',
  border:     '#E5E7EB',
  bg:         '#F5F7FA',
  muted:      '#9ca3af',
  sub:        '#6b7280',
  body:       '#1f2937',
};

// Same self-owned-input pattern as MarksInput in RecordMarks.tsx — keeps its
// own localValue so typing never gets clobbered by a parent re-render.
interface SubjectRowInputProps {
  subjectId: string;
  field: MarkField;
  initialValue: string;
  max: number;
  onChange: (subjectId: string, field: MarkField, value: string) => void;
}

const SubjectRowInput: React.FC<SubjectRowInputProps> = React.memo(({ subjectId, field, initialValue, max, onChange }) => {
  const [localValue, setLocalValue] = useState(initialValue);
  const numericValue = localValue === '' ? null : parseFloat(localValue);
  const isOverMax = numericValue !== null && !Number.isNaN(numericValue) && numericValue > max;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    if (raw !== '' && !/^\d*\.?\d*$/.test(raw)) return;
    setLocalValue(raw);
    onChange(subjectId, field, raw);
  };

  return (
    <div style={{ width: '100px' }}>
      <GlobalInputField
        value={localValue}
        onChange={handleChange}
        placeholder={`Max ${max}`}
        type="text"
        fullWidth={true}
        error={isOverMax ? `Exceeds max (${max})` : ''}
      />
    </div>
  );
});
SubjectRowInput.displayName = 'SubjectRowInput';

interface AllSubjectsMarksModalProps {
  visible: boolean;
  onHide: () => void;
  student: StudentLite | null;
  batchId: string | null;
  subjects: Subject[];
  instituteId?: string;
  branchId?: string;
  onSaved: () => void;
}

const AllSubjectsMarksModal: React.FC<AllSubjectsMarksModalProps> = ({
  visible,
  onHide,
  student,
  batchId,
  subjects,
  instituteId,
  branchId,
  onSaved,
}) => {
  const toast = useRef<Toast>(null);

  const [marksMap, setMarksMap] = useState<Record<string, SubjectMarksEntry>>({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);

  const showToast = (severity: 'success' | 'error' | 'warn' | 'info', summary: string, detail: string, life = 3500) =>
    toast.current?.show({ severity, summary, detail, life });

  // Pulls this student's marks across ALL subjects by hitting the existing
  // per-subject endpoint once per subject (in parallel) and picking out the
  // matching student_id from each response. No backend change required.
  const loadAllSubjectMarks = useCallback(async () => {
    if (!student || !batchId || subjects.length === 0) return;
    setLoading(true);
    setError(null);
    try {
      const responses = await Promise.all(
        subjects.map((s) =>
          apiClient
            .get(`/v1/marks/batch/${batchId}/subject/${s.subject_id}`)
            .then((res) => ({ subjectId: s.subject_id, res }))
            .catch(() => ({ subjectId: s.subject_id, res: null }))
        )
      );

      const map: Record<string, SubjectMarksEntry> = {};
      responses.forEach(({ subjectId, res }) => {
        const list = res?.data?.success ? res.data.data : [];
        const entry = Array.isArray(list)
          ? list.find((item: any) => item.student_id === student.student_id)
          : null;
        map[subjectId] = {
          theory:
            entry?.marks?.marks_theory !== null && entry?.marks?.marks_theory !== undefined
              ? String(entry.marks.marks_theory)
              : '',
          practical:
            entry?.marks?.marks_practical !== null && entry?.marks?.marks_practical !== undefined
              ? String(entry.marks.marks_practical)
              : '',
          updatedAt: entry?.marks?.updated_at ?? null,
        };
      });
      setMarksMap(map);
    } catch (err) {
      setError('Failed to load existing marks for this student.');
      showToast('error', 'Error', 'Failed to load existing marks for this student.');
    } finally {
      setLoading(false);
    }
  }, [student, batchId, subjects]);

  useEffect(() => {
    if (visible) {
      setSaveSuccess(false);
      loadAllSubjectMarks();
    } else {
      setMarksMap({});
      setError(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, student?.student_id, batchId]);

  const handleFieldChange = useCallback((subjectId: string, field: MarkField, value: string) => {
    setMarksMap((prev) => ({
      ...prev,
      [subjectId]: {
        theory: field === 'theory' ? value : prev[subjectId]?.theory ?? '',
        practical: field === 'practical' ? value : prev[subjectId]?.practical ?? '',
        updatedAt: prev[subjectId]?.updatedAt,
      },
    }));
  }, []);

  const handleSaveAll = async () => {
    if (!student || !batchId) return;
    if (!instituteId || !branchId) {
      setError('Institute/branch information missing. Please wait for profile to load and try again.');
      showToast('error', 'Missing Info', 'Institute/branch information missing. Please try again.');
      return;
    }

    const validationErrors: string[] = [];
    subjects.forEach((s) => {
      const entry = marksMap[s.subject_id];
      if (!entry) return;
      const theoryVal = entry.theory === '' ? null : parseFloat(entry.theory);
      const practicalVal = entry.practical === '' ? null : parseFloat(entry.practical);
      if (theoryVal !== null && theoryVal > s.max_marks) {
        validationErrors.push(`${s.subject_name}: Theory exceeds max (${s.max_marks})`);
      }
      if (practicalVal !== null && practicalVal > s.practical_max_marks) {
        validationErrors.push(`${s.subject_name}: Practical exceeds max (${s.practical_max_marks})`);
      }
    });
    if (validationErrors.length > 0) {
      setError('Validation errors:\n' + validationErrors.join('\n'));
      showToast('error', 'Validation Failed', 'Please correct the highlighted subjects');
      return;
    }

    setSaving(true);
    setError(null);
    let failed = 0;
    let success = 0;

    try {
      const promises = subjects.map(async (s) => {
        const entry = marksMap[s.subject_id];
        if (!entry) return;
        const theoryVal = entry.theory === '' ? null : parseFloat(entry.theory);
        const practicalVal = entry.practical === '' ? null : parseFloat(entry.practical);
        if (theoryVal === null && practicalVal === null) return;

        const payload = {
          student_id: student.student_id,
          batch_id: batchId,
          subject_id: s.subject_id,
          marks_theory: theoryVal ?? 0,
          marks_practical: practicalVal ?? 0,
          remarks: '',
          institute_id: instituteId,
          branch_id: branchId,
        };

        try {
          await apiClient.post('/v1/marks', payload);
          success++;
        } catch (err) {
          failed++;
        }
      });

      await Promise.all(promises);

      if (failed === 0) {
        setSaveSuccess(true);
        showToast('success', 'Marks Saved', 'All subject marks saved successfully.');
        onSaved();
        setTimeout(() => {
          setSaveSuccess(false);
          onHide();
        }, 1200);
      } else {
        const msg = `Saved ${success} subjects, failed ${failed}. Please review and retry.`;
        setError(msg);
        showToast('error', 'Partial Save', msg);
      }
    } catch (err) {
      setError('An error occurred while saving.');
      showToast('error', 'Error', 'An error occurred while saving.');
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    setError(null);
    setSaveSuccess(false);
    onHide();
  };

  const enteredCount = subjects.filter((s) => {
    const entry = marksMap[s.subject_id];
    return entry && (entry.theory !== '' || entry.practical !== '');
  }).length;

  return (
    <Dialog
      visible={visible}
      onHide={handleClose}
      style={{ width: '750px', maxWidth: '95vw' }}
      modal
      dismissableMask
      draggable={false}
      pt={{
        root:    { style: { borderRadius: '20px', overflow: 'hidden', boxShadow: '0 25px 50px -12px rgba(14,34,93,0.25)' } },
        header:  { style: { display: 'none' } },
        content: { style: { padding: '22px' } }
      }}
    >
      <Toast ref={toast} position="top-right" />

      <div>
        {/* ── Header (compacted) ─────────────────────────────────────── */}
        <div style={{
          background: `linear-gradient(180deg, ${C.navy} 0%, ${C.navyMid} 50%, ${C.navyDark} 100%)`,
          borderRadius: '12px', padding: '21px', marginBottom: '18px',
          color: C.white, position: 'relative', overflow: 'hidden'
        }}>
          <div style={{ position: 'absolute', top: '-38px', right: '-38px', width: '135px', height: '135px', background: C.goldDim, borderRadius: '50%', filter: 'blur(40px)', pointerEvents: 'none' }} />
          <div style={{ position: 'absolute', bottom: '-30px', left: '20%', width: '90px', height: '90px', background: 'rgba(251,178,31,0.05)', borderRadius: '50%', filter: 'blur(30px)', pointerEvents: 'none' }} />

          <div style={{ position: 'relative', zIndex: 1 }}>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', padding: '3px 9px', background: C.goldDim, border: `1px solid ${C.goldBorder}`, borderRadius: '15px', marginBottom: '9px', fontSize: '0.51rem', fontWeight: '700', letterSpacing: '0.6px', textTransform: 'uppercase', color: C.gold }}>
              <i className="pi pi-book" style={{ fontSize: '0.53rem' }} />
              Marks Entry
            </div>

            {student ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: '11px' }}>
                <Avatar
                  image={student.profilePicture?.cdn_url}
                  icon={!student.profilePicture ? 'pi pi-user' : undefined}
                  shape="circle"
                  style={{ width: '35px', height: '35px', backgroundColor: C.goldDim, color: C.gold, border: `1px solid ${C.goldBorder}`, flexShrink: 0 }}
                />
                <div>
                  <h2 style={{ margin: '0 0 2px 0', fontSize: '1.02rem', fontWeight: '700', color: C.white, letterSpacing: '-0.5px' }}>
                    {student.name}
                  </h2>
                  <p style={{ margin: 0, color: 'rgba(255,255,255,0.6)', fontSize: '0.62rem' }}>
                    {student.student_custom_id ? `ID: ${student.student_custom_id}` : 'Enter subject-wise marks below'}
                  </p>
                </div>
              </div>
            ) : (
              <h2 style={{ margin: '0 0 5px 0', fontSize: '1.13rem', fontWeight: '700', color: C.white, letterSpacing: '-0.5px' }}>
                Enter Marks
              </h2>
            )}
          </div>
        </div>

        {error && <Message severity="error" text={error} style={{ width: '100%', whiteSpace: 'pre-line', marginBottom: '18px', fontSize: '0.81rem' }} />}
        {saveSuccess && <Message severity="success" text="All subject marks saved!" style={{ width: '100%', marginBottom: '18px', fontSize: '0.81rem' }} />}

        {/* ── Body ────────────────────────────────────────────────────── */}
        <div style={{ background: C.white, borderRadius: '16px', padding: '24px', marginBottom: '20px', border: `1px solid ${C.border}`, boxShadow: '0 2px 8px rgba(14,34,93,0.06)' }}>
          <SectionBox
            title="Subject Marks"
            icon="pi-list"
            badge={!loading ? `${enteredCount}/${subjects.length} entered` : undefined}
          >
            {loading ? (
              <div style={{ padding: '40px', textAlign: 'center' }}>
                <ProgressSpinner style={{ width: '40px', height: '40px' }} strokeWidth="3" />
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 110px 110px 70px',
                    gap: '10px',
                    padding: '8px 10px',
                    background: C.bg,
                    borderRadius: '8px 8px 0 0',
                    borderBottom: `2px solid ${C.border}`,
                    fontSize: '0.7rem',
                    fontWeight: 700,
                    color: C.muted,
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                  }}
                >
                  <div>Subject</div>
                  <div style={{ textAlign: 'center' }}>Theory</div>
                  <div style={{ textAlign: 'center' }}>Practical</div>
                  <div style={{ textAlign: 'center' }}>Total</div>
                </div>

                {subjects.length === 0 && (
                  <div style={{ padding: '20px', textAlign: 'center', color: C.muted, fontSize: '0.85rem' }}>
                    No subjects found for this course.
                  </div>
                )}

                {subjects.map((s) => {
                  const entry = marksMap[s.subject_id];
                  const theoryNum = entry?.theory ? parseFloat(entry.theory) : 0;
                  const practicalNum = entry?.practical ? parseFloat(entry.practical) : 0;
                  const total =
                    (Number.isNaN(theoryNum) ? 0 : theoryNum) + (Number.isNaN(practicalNum) ? 0 : practicalNum);
                  const hasAny = entry && (entry.theory !== '' || entry.practical !== '');

                  return (
                    <div
                      key={s.subject_id}
                      style={{
                        display: 'grid',
                        gridTemplateColumns: '1fr 110px 110px 70px',
                        gap: '10px',
                        padding: '10px',
                        alignItems: 'center',
                        background: C.white,
                        borderBottom: `1px solid ${C.border}`,
                      }}
                    >
                      <div style={{ fontSize: '0.85rem', fontWeight: 600, color: C.navy }}>
                        {s.subject_name}
                        <div style={{ fontSize: '0.65rem', fontWeight: 400, color: C.muted }}>
                          Max T{s.max_marks}/P{s.practical_max_marks}
                        </div>
                      </div>
                      <SubjectRowInput
                        key={`${s.subject_id}-theory-${entry?.updatedAt ?? 'new'}`}
                        subjectId={s.subject_id}
                        field="theory"
                        initialValue={entry?.theory ?? ''}
                        max={s.max_marks}
                        onChange={handleFieldChange}
                      />
                      <SubjectRowInput
                        key={`${s.subject_id}-practical-${entry?.updatedAt ?? 'new'}`}
                        subjectId={s.subject_id}
                        field="practical"
                        initialValue={entry?.practical ?? ''}
                        max={s.practical_max_marks}
                        onChange={handleFieldChange}
                      />
                      <div style={{ textAlign: 'center', fontWeight: 700, color: hasAny ? C.navy : '#d1d5db' }}>
                        {hasAny ? total : '-'}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </SectionBox>
        </div>

        {/* ── Actions ─────────────────────────────────────────────────── */}
        <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
          <GlobalButton label="Cancel" variant="outline" onClick={handleClose} disabled={saving} />
          <GlobalButton
            label={saving ? 'Saving...' : 'Save All Subjects'}
            variant="primary"
            onClick={handleSaveAll}
            disabled={saving || loading || subjects.length === 0}
            loading={saving}
          />
        </div>
      </div>
    </Dialog>
  );
};

// ── SectionBox (shared visual pattern with AddNoticeModal) ─────────────────
const SectionBox: React.FC<{ title: string; icon: string; badge?: string; children: React.ReactNode }> = ({ title, icon, badge, children }) => (
  <div style={{ background: '#F5F7FA', borderRadius: '14px', padding: '18px', border: '1px solid #E5E7EB' }}>
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
        <div style={{ width: '32px', height: '32px', background: 'linear-gradient(135deg, #0e225d 0%, #1a3370 100%)', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <i className={`pi ${icon}`} style={{ color: '#fbb21f', fontSize: '0.85rem' }} />
        </div>
        <span style={{ fontSize: '0.88rem', fontWeight: '700', color: '#0e225d' }}>{title}</span>
      </div>
      {badge && <span style={{ fontSize: '0.72rem', color: '#9ca3af', fontWeight: '600' }}>{badge}</span>}
    </div>
    {children}
  </div>
);

export default AllSubjectsMarksModal;